<div class="gw-space-y-6">
  <gatewee-stepper
    [steps]="wizardSteps"
    [(activeIndex)]="step"
    [maxAllowedIndex]="maxAllowedIndex"
    [canNavigate]="canNavigateStepper"
  ></gatewee-stepper>

  <!-- PIX key lookup (3-step flow only) -->
  <form
    *ngIf="showPixLookupStep && step === 0"
    [formGroup]="lookupForm"
    (ngSubmit)="submitLookup()"
    class="gw-space-y-4"
  >
    <p class="gw-text-sm gw-text-text-secondary gw-m-0">
      {{
        'features.recipientsBankAccounts.form.registration.lookup.description'
          | translate
      }}
    </p>

    <gatewee-input-v2
      [form]="lookupForm"
      controlName="pixKey"
      label="features.recipientsBankAccounts.form.pixKeyLabel"
      id="bank-account-registration-lookup-pix-key"
      [required]="true"
      [mask]="lookupPixKeyMask"
      [placeholder]="
        'features.recipientsBankAccounts.form.pixKeyPlaceholder' | translate
      "
      [customMessages]="lookupPixKeyCustomMessages"
      (blur)="formatLookupPixKeyOnBlur()"
    ></gatewee-input-v2>

    <div
      *ngIf="showAmbiguousKindRow"
      class="gw-mt-2 gw-flex gw-flex-wrap gw-items-center gw-gap-2"
    >
      <span class="gw-text-text-secondary gw-text-xs gw-font-medium gw-m-0">
        {{
          'features.recipientsBankAccounts.form.registration.lookup.kindAmbiguousPrompt'
            | translate
        }}
      </span>
      <button
        type="button"
        class="gw-inline-flex gw-items-center gw-rounded-full gw-border gw-border-border-normal gw-bg-background-elevation-1 gw-px-3 gw-py-1 gw-text-xs gw-font-semibold gw-text-text-primary hover:gw-bg-background-neutral-1 gw-cursor-pointer"
        [class.gw-border-background-accent-1]="manualLookupKind === 'cpf'"
        (click)="pickAmbiguousLookupKind('cpf')"
      >
        {{
          'features.recipientsBankAccounts.form.registration.lookup.kindChipCpf'
            | translate
        }}
      </button>
      <button
        type="button"
        class="gw-inline-flex gw-items-center gw-rounded-full gw-border gw-border-border-normal gw-bg-background-elevation-1 gw-px-3 gw-py-1 gw-text-xs gw-font-semibold gw-text-text-primary hover:gw-bg-background-neutral-1 gw-cursor-pointer"
        [class.gw-border-background-accent-1]="manualLookupKind === 'phone'"
        (click)="pickAmbiguousLookupKind('phone')"
      >
        {{
          'features.recipientsBankAccounts.form.registration.lookup.kindChipPhone'
            | translate
        }}
      </button>
    </div>

    <p
      *ngIf="lookupErrorMessage"
      class="gw-m-0 gw-rounded-lg gw-bg-status-negative-pale gw-p-4 gw-text-sm gw-text-status-negative"
    >
      {{ lookupErrorMessage }}
    </p>

    <div class="gw-flex gw-justify-end gw-gap-4">
      <gatewee-button
        *ngIf="lookupNotFound"
        type="button"
        [title]="
          'features.recipientsBankAccounts.form.registration.lookup.goToManual'
            | translate
        "
        variant="secondary"
        [disabled]="lookupSubmitting"
        (clicked)="goToManualFromLookup()"
      ></gatewee-button>
      <gatewee-button
        type="button"
        [title]="
          lookupSubmitting
            ? ('common.actions.loading' | translate)
            : ('features.recipientsBankAccounts.form.registration.lookup.submit'
              | translate)
        "
        variant="primary"
        [disabled]="!canSubmitLookup"
        (clicked)="submitLookup()"
      ></gatewee-button>
    </div>
  </form>

  <!-- Account data -->
  <form
    *ngIf="step === accountDataStepIndex && reviewSource !== 'lookup'"
    [formGroup]="manualForm"
    (ngSubmit)="continueFromAccountData()"
    class="gw-space-y-6"
  >
    <p
      *ngIf="capabilityUnavailable"
      class="gw-m-0 gw-rounded-lg gw-bg-background-elevation-2 gw-p-4 gw-text-sm gw-text-text-secondary"
    >
      {{
        'features.recipientsBankAccounts.form.registration.errors.unavailable'
          | translate
      }}
    </p>

    <div class="gw-grid gw-grid-cols-1 md:gw-grid-cols-2 gw-gap-4">
      <gatewee-select-v3
        formControlName="pixKeyType"
        [label]="
          'features.recipientsBankAccounts.form.pixKeyTypeLabel' | translate
        "
        [placeholder]="
          'features.recipientsBankAccounts.form.pixKeyTypePlaceholder'
            | translate
        "
        [items]="pixKeyTypeOptions"
        [required]="true"
      ></gatewee-select-v3>

      <gatewee-input-v2
        [form]="manualForm"
        controlName="pixKey"
        label="features.recipientsBankAccounts.form.pixKeyLabel"
        id="bank-account-registration-manual-pix-key"
        [required]="true"
        [mask]="manualPixKeyMask"
        [max]="manualPixKeyMaxLength"
        [placeholder]="
          'features.recipientsBankAccounts.form.pixKeyPlaceholder' | translate
        "
        [customMessages]="manualPixKeyCustomMessages"
      ></gatewee-input-v2>
    </div>

    <div class="gw-grid gw-grid-cols-1 md:gw-grid-cols-2 gw-gap-4">
      <gatewee-input-v2
        [form]="manualForm"
        controlName="holderName"
        label="features.recipientsBankAccounts.form.holderNameLabel"
        id="bank-account-registration-manual-holder-name"
        [required]="true"
        [placeholder]="
          'features.recipientsBankAccounts.form.holderNamePlaceholder'
            | translate
        "
        [customMessages]="manualHolderNameCustomMessages"
      ></gatewee-input-v2>

      <gatewee-input-v2
        [form]="manualForm"
        controlName="holderDocument"
        label="features.recipientsBankAccounts.form.holderDocumentLabel"
        id="bank-account-registration-manual-holder-document"
        [required]="true"
        [mask]="holderDocumentMask"
        [max]="holderDocumentMaxLength"
        [placeholder]="
          'features.recipientsBankAccounts.form.holderDocumentPlaceholder'
            | translate
        "
        [customMessages]="manualHolderDocumentCustomMessages"
        (blur)="formatManualHolderDocumentOnBlur()"
      ></gatewee-input-v2>
    </div>

    <div class="gw-grid gw-grid-cols-1 md:gw-grid-cols-2 gw-gap-4">
      <gatewee-input-v2
        [form]="manualForm"
        controlName="compeCode"
        label="features.recipientsBankAccounts.form.registration.manual.compeCode"
        id="bank-account-registration-manual-compe-code"
        [placeholder]="
          'features.recipientsBankAccounts.form.registration.manual.compeCodePlaceholder'
            | translate
        "
      ></gatewee-input-v2>

      <gatewee-select-v3
        formControlName="accountType"
        [label]="
          'features.recipientsBankAccounts.form.registration.manual.accountType'
            | translate
        "
        [placeholder]="
          'features.recipientsBankAccounts.form.registration.manual.accountTypePlaceholder'
            | translate
        "
        [items]="accountTypeOptions"
      ></gatewee-select-v3>
    </div>

    <div class="gw-grid gw-grid-cols-1 md:gw-grid-cols-2 gw-gap-4">
      <div class="gw-flex gw-gap-2">
        <gatewee-input-v2
          class="gw-flex-1"
          [form]="manualForm"
          controlName="agency"
          label="features.recipientsBankAccounts.form.registration.manual.agency"
          id="bank-account-registration-manual-agency"
          [placeholder]="
            'features.recipientsBankAccounts.form.registration.manual.agencyPlaceholder'
              | translate
          "
        ></gatewee-input-v2>
        <gatewee-input-v2
          class="gw-w-24 gw-shrink-0"
          [form]="manualForm"
          controlName="agencyDigit"
          label="features.recipientsBankAccounts.form.registration.manual.agencyDigit"
          id="bank-account-registration-manual-agency-digit"
          [placeholder]="
            'features.recipientsBankAccounts.form.registration.manual.agencyDigitPlaceholder'
              | translate
          "
        ></gatewee-input-v2>
      </div>

      <div class="gw-flex gw-gap-2">
        <gatewee-input-v2
          class="gw-flex-1"
          [form]="manualForm"
          controlName="account"
          label="features.recipientsBankAccounts.form.registration.manual.account"
          id="bank-account-registration-manual-account"
          [placeholder]="
            'features.recipientsBankAccounts.form.registration.manual.accountPlaceholder'
              | translate
          "
        ></gatewee-input-v2>
        <gatewee-input-v2
          class="gw-w-24 gw-shrink-0"
          [form]="manualForm"
          controlName="accountDigit"
          label="features.recipientsBankAccounts.form.registration.manual.accountDigit"
          id="bank-account-registration-manual-account-digit"
          [placeholder]="
            'features.recipientsBankAccounts.form.registration.manual.accountDigitPlaceholder'
              | translate
          "
        ></gatewee-input-v2>
      </div>
    </div>

    <div
      class="gw-flex gw-items-center gw-gap-medium gw-rounded-lg gw-border gw-border-status-info gw-bg-status-info-pale gw-p-medium gw-text-sm gw-text-text-primary"
      role="status"
    >
      <gatewee-icon
        icon="@phi.info"
        size="1.25rem"
        class="gw-shrink-0 gw-text-status-info"
      ></gatewee-icon>
      <p class="gw-m-0">
        {{ 'features.recipientsBankAccounts.form.defaultHint' | translate }}
      </p>
    </div>

    <div class="gw-flex gw-justify-end gw-gap-4">
      <gatewee-button
        *ngIf="showAccountDataBackToPix; else accountDataCancelBtn"
        type="button"
        [title]="'common.actions.back' | translate"
        variant="secondary"
        (clicked)="backToPixKeyStep()"
      ></gatewee-button>
      <ng-template #accountDataCancelBtn>
        <gatewee-button
          type="button"
          [title]="'common.actions.cancel' | translate"
          variant="secondary"
          (clicked)="cancel()"
        ></gatewee-button>
      </ng-template>
      <gatewee-button
        type="button"
        [title]="
          'features.recipientsBankAccounts.form.registration.manual.continue'
            | translate
        "
        variant="primary"
        [disabled]="manualForm.invalid"
        (clicked)="continueFromAccountData()"
      ></gatewee-button>
    </div>
  </form>

  <!-- Review -->
  <div *ngIf="step === reviewStepIndex && reviewPayload" class="gw-space-y-4">
    <p class="gw-text-sm gw-text-text-secondary gw-m-0">
      {{
        'features.recipientsBankAccounts.form.registration.review.description'
          | translate
      }}
    </p>

    <div class="gw-rounded-xl gw-bg-background-elevation-1 gw-p-medium">
      <div
        class="gw-grid gw-grid-cols-1 md:gw-grid-cols-2 lg:gw-grid-cols-3 gw-gap-4"
      >
        <div
          class="gw-bg-background-elevation-2 gw-rounded-lg gw-p-4 gw-border-base-neutral"
        >
          <label
            class="gw-text-sm gw-font-medium gw-text-text-secondary gw-block gw-mb-2"
          >
            {{ 'features.recipientsBankAccounts.form.pixKeyLabel' | translate }}
          </label>
          <p
            class="gw-m-0 gw-text-base gw-text-text-primary gw-font-medium gw-break-all"
          >
            {{ reviewPayload.pixKey }}
          </p>
          <p
            *ngIf="reviewPayload.pixKeyType"
            class="gw-m-0 gw-mt-1 gw-text-sm gw-text-text-secondary"
          >
            {{ 'enums.pixKeyTypes.' + reviewPayload.pixKeyType | translate }}
          </p>
        </div>

        <div
          class="gw-bg-background-elevation-2 gw-rounded-lg gw-p-4 gw-border-base-neutral"
        >
          <label
            class="gw-text-sm gw-font-medium gw-text-text-secondary gw-block gw-mb-2"
          >
            {{
              'features.recipientsBankAccounts.form.holderNameLabel' | translate
            }}
          </label>
          <p class="gw-m-0 gw-text-base gw-text-text-primary gw-font-medium">
            {{ reviewPayload.holderName || '—' }}
          </p>
        </div>

        <div
          class="gw-bg-background-elevation-2 gw-rounded-lg gw-p-4 gw-border-base-neutral"
        >
          <label
            class="gw-text-sm gw-font-medium gw-text-text-secondary gw-block gw-mb-2"
          >
            {{
              'features.recipientsBankAccounts.form.holderDocumentLabel'
                | translate
            }}
          </label>
          <p class="gw-m-0 gw-text-base gw-text-text-primary gw-font-medium">
            {{
              reviewPayload.holderDocument
                ? (reviewPayload.holderDocument | document : true)
                : '—'
            }}
          </p>
        </div>

        <div
          *ngIf="reviewPayload.compeCode || reviewBankName"
          class="gw-bg-background-elevation-2 gw-rounded-lg gw-p-4 gw-border-base-neutral"
        >
          <label
            class="gw-text-sm gw-font-medium gw-text-text-secondary gw-block gw-mb-2"
          >
            {{
              'features.recipientsBankAccounts.form.registration.manual.compeCode'
                | translate
            }}
          </label>
          <div class="gw-flex gw-items-center gw-gap-3">
            <gatewee-bank-svg-logo
              *ngIf="reviewPayload.compeCode"
              [compeCode]="reviewPayload.compeCode"
              [size]="36"
              class="gw-inline-flex gw-h-9 gw-w-9 gw-shrink-0 gw-items-center gw-justify-center"
            ></gatewee-bank-svg-logo>
            <p
              class="gw-m-0 gw-min-w-0 gw-text-base gw-text-text-primary gw-font-medium"
            >
              <ng-container
                *ngIf="
                  reviewBankName && reviewBankName !== reviewPayload.compeCode;
                  else reviewCompeOnly
                "
              >
                {{ reviewBankName }} ({{ reviewPayload.compeCode }})
              </ng-container>
              <ng-template #reviewCompeOnly>
                {{ reviewPayload.compeCode || reviewBankName }}
              </ng-template>
            </p>
          </div>
        </div>

        <div
          *ngIf="reviewAgencyDisplay"
          class="gw-bg-background-elevation-2 gw-rounded-lg gw-p-4 gw-border-base-neutral"
        >
          <label
            class="gw-text-sm gw-font-medium gw-text-text-secondary gw-block gw-mb-2"
          >
            {{
              'features.recipientsBankAccounts.form.registration.manual.agency'
                | translate
            }}
          </label>
          <p class="gw-m-0 gw-text-base gw-text-text-primary gw-font-medium">
            {{ reviewAgencyDisplay }}
          </p>
        </div>

        <div
          *ngIf="reviewAccountDisplay"
          class="gw-bg-background-elevation-2 gw-rounded-lg gw-p-4 gw-border-base-neutral"
        >
          <label
            class="gw-text-sm gw-font-medium gw-text-text-secondary gw-block gw-mb-2"
          >
            {{
              'features.recipientsBankAccounts.form.registration.manual.account'
                | translate
            }}
          </label>
          <p class="gw-m-0 gw-text-base gw-text-text-primary gw-font-medium">
            {{ reviewAccountDisplay }}
          </p>
        </div>

        <div
          *ngIf="reviewPayload.accountType"
          class="gw-bg-background-elevation-2 gw-rounded-lg gw-p-4 gw-border-base-neutral"
        >
          <label
            class="gw-text-sm gw-font-medium gw-text-text-secondary gw-block gw-mb-2"
          >
            {{
              'features.recipientsBankAccounts.form.registration.manual.accountType'
                | translate
            }}
          </label>
          <p class="gw-m-0 gw-text-base gw-text-text-primary gw-font-medium">
            {{ getAccountTypeLabel(reviewPayload.accountType) }}
          </p>
        </div>
      </div>
    </div>

    <div class="gw-flex gw-justify-end gw-gap-4">
      <gatewee-button
        type="button"
        [title]="'common.actions.back' | translate"
        variant="secondary"
        [disabled]="reviewSubmitting"
        (clicked)="backFromReview()"
      ></gatewee-button>
      <gatewee-button
        type="button"
        [title]="
          reviewSubmitting
            ? ('common.actions.loading' | translate)
            : ('features.recipientsBankAccounts.form.registration.review.confirm'
              | translate)
        "
        variant="primary"
        [disabled]="reviewSubmitting"
        (clicked)="confirmReview()"
      ></gatewee-button>
    </div>
  </div>
</div>
